"use client"

import styled from "@emotion/styled"
import { getNonUmbracoUrl, getUmbracoMediaUrl, Link } from "@eurodrive/utils"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Drawer,
  TextField,
  Typography,
} from "@mui/material"
import Image from "next/image"
import React from "react"
import AccordionSlash from "../../icons/AccordionSlash"
import ArrowUp from "../../icons/ArrowDown"
import { MenuItemData } from "./Header"

const StyledDrawer = styled(Drawer)`
  .MuiPaper-root {
    padding-top: 64px;
    ${(p) => p.theme.breakpoints.up("md")} {
      padding-top: 80px;
    }
    padding-bottom: 32px;
    height: 100%;
    background: white;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
    ${(p) => p.theme.breakpoints.down("sm")} {
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.4);
    height: 100vh;
  }
  z-index: 1000000 !important;
`
const StyledDrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 16px;
`

const LinksDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 20px;
`

const StyledAccordion = styled(Accordion)<{ index: number }>`
  padding: 0 !important;
  box-shadow: none !important;
  ::before {
    display: none;
  }
  &.Mui-expanded {
    margin: 0 !important;
  }
  & .MuiAccordionSummary-root {
    min-height: 0 !important;
    padding: 12px 0 !important;
  }
  & .MuiAccordionSummary-content {
    margin: 0 !important;
  }
  & .MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 16px !important;
    background-color: #f5f6f8;
    border-radius: 8px;
  }
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: black;
  font-size: 16px;
  line-height: 24px;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledTextField = styled(TextField)`
  width: 328px;
  & .MuiInputBase-root {
    border-radius: 12px;
  }
  & .MuiInputBase-input {
    margin-left: 8px;
    font-size: 16px;
    line-height: 24px;
    ::placeholder {
      color: #6f747d;
      font-size: 16px;
      line-height: 24px;
    }
  }
`

const BottomContent = styled.div`
  padding: 16px 20px 0px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledSocialLink = styled(Link)`
  text-decoration: none;
  display: flex;
  gap: 8px;
  justify-content: center;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  justify-content: center;
`

type SideDrawerProps = {
  isOpen: boolean
  toggleDrawer: (
    open: boolean,
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  menusmap: MenuItemData[]
  locale: string
  socialLinks: {
    icon: string
    url: string
    name: string
  }[]
}

const SideDrawer = ({
  isOpen,
  toggleDrawer,
  menusmap,
  setIsDrawerOpen,
  socialLinks,
}: SideDrawerProps): React.ReactElement => {
  const [expandedAccordion, setExpandedAccordion] = React.useState<
    number | false
  >(false)

  const handleClose = () => {
    setIsDrawerOpen(false)
  }

  const handleChange =
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? index : false)
    }

  return (
    <StyledDrawer anchor={"top"} open={isOpen} onClose={handleClose}>
      <StyledDrawerWrapper role="presentation">
        <LinksDiv>
          {menusmap.map((item, i) => {
            const selfLink = item.href && !item.items.length
            return item?.items?.length > 0 ? (
              <StyledAccordion
                key={i}
                index={i}
                expanded={expandedAccordion === i}
                onChange={selfLink ? undefined : handleChange(i)}
                slotProps={{
                  heading: {
                    component: "div",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    expandedAccordion === i ? <AccordionSlash /> : <ArrowUp />
                  }
                  aria-controls={`panel${i}-content`}
                  id={`panel${i}-header`}
                >
                  {selfLink ? (
                    <StyledLink
                      onClick={handleClose}
                      href={getNonUmbracoUrl(item.href)}
                    >
                      {item.label}
                    </StyledLink>
                  ) : (
                    <Typography
                      variant="body1"
                      fontWeight={expandedAccordion === i ? 600 : 400}
                    >
                      {item.label}
                    </Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  {item.items.map((el, x) => {
                    const href = getNonUmbracoUrl(el.href)
                    return (
                      <StyledLink onClick={handleClose} href={href} key={x}>
                        {el.label}
                      </StyledLink>
                    )
                  })}
                </AccordionDetails>
              </StyledAccordion>
            ) : (
              (() => {
                const baseHref = getNonUmbracoUrl(item.href)
                return (
                  <StyledLink
                    key={i}
                    onClick={handleClose}
                    href={baseHref}
                    target={selfLink ? "_self" : "_blank"}
                    style={{
                      padding: "12px 0",
                    }}
                  >
                    {item.label}
                  </StyledLink>
                )
              })()
            )
          })}
        </LinksDiv>
        <BottomContent>
          <ButtonsContainer>
            <Button
              variant="primary"
              size="large"
              LinkComponent={Link}
              href="/calculator"
              fullWidth
              onClick={handleClose}
            >
              אפשרויות מימון
            </Button>
            <Typography style={{ textAlign: "center" }} variant="body1">
              עקוב אחרינו
            </Typography>
          </ButtonsContainer>
          <SocialLinks>
            {socialLinks.map((link, i) => (
              <StyledLink
                href={getNonUmbracoUrl(link.url || "")}
                target="_blank"
                key={i}
              >
                <Image
                  src={getUmbracoMediaUrl(link.icon)}
                  alt={""}
                  width={24}
                  height={24}
                />
              </StyledLink>
            ))}
          </SocialLinks>
        </BottomContent>
      </StyledDrawerWrapper>
    </StyledDrawer>
  )
}

export default SideDrawer
